/**
 * Notification bell — server-backed inbox with filters, date groups, SPA navigation.
 * Click marks read and navigates; notifications are kept until user clears them.
 */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import {
  Bell,
  X,
  Package,
  MessageCircle,
  Tag,
  Info,
  Trash2,
  CheckCheck,
  Loader2,
} from 'lucide-react';
import {
  NOTIFICATION_FILTERS,
  matchesNotificationFilter,
  groupNotificationsByDate,
  getNotificationBody,
  getNotificationNavigation,
} from '../../utils/notificationHelpers';
import './NotificationDropdown.css';

const TYPE_ICONS = {
  order: Package,
  offer: Tag,
  bid: Tag,
  message: MessageCircle,
  product: Package,
  account: Info,
  promotional: Info,
  system: Info,
};

function formatNotificationTime(dateString) {
  if (!dateString) return 'Just now';
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInMins = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMs / 3600000);
  const diffInDays = Math.floor(diffInMs / 86400000);

  if (diffInMins < 1) return 'Just now';
  if (diffInMins < 60) return `${diffInMins}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return date.toLocaleDateString();
}

const NotificationDropdown = React.memo(function NotificationDropdown() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const dropdownRef = useRef(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    clearAll,
    isDeleting,
  } = useNotifications();

  useEffect(() => {
    window.__notificationNavigate = (link, data) => {
      const fake = { actionUrl: link, data, type: data?.type };
      const target = getNotificationNavigation(fake);
      navigate(target.pathname, { state: target.state, search: target.search });
    };
    return () => {
      delete window.__notificationNavigate;
    };
  }, [navigate]);

  const filtered = useMemo(
    () => notifications.filter((n) => matchesNotificationFilter(n, activeFilter)),
    [notifications, activeFilter]
  );

  const groups = useMemo(() => groupNotificationsByDate(filtered), [filtered]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      const id = notification._id || notification.id;
      if (!notification.isRead && id) {
        try {
          await markAsRead(id);
        } catch (e) {
          console.error('Failed to mark notification as read:', e);
        }
      }
      setIsOpen(false);
      const target = getNotificationNavigation(notification);
      navigate(target.pathname, { state: target.state, search: target.search });
    },
    [markAsRead, navigate]
  );

  const renderGroup = (label, items) => {
    if (!items.length) return null;
    return (
      <div key={label} className="notification-group">
        <p className="notification-group__label">{label}</p>
        {items.map((notification) => {
          const Icon = TYPE_ICONS[notification.type] || Bell;
          const id = notification._id || notification.id;
          return (
            <div
              key={id}
              role="button"
              tabIndex={0}
              className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
              onClick={() => handleNotificationClick(notification)}
              onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
            >
              <div className="notification-icon">
                <Icon size={18} />
              </div>
              <div className="notification-content">
                <p className="notification-title">{notification.title}</p>
                <p className="notification-message">{getNotificationBody(notification)}</p>
                <span className="notification-time">
                  {formatNotificationTime(notification.createdAt)}
                </span>
              </div>
              {!notification.isRead && <span className="notification-unread-dot" aria-hidden />}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="notification-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-header__actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="mark-all-read-btn"
                  onClick={() => markAllAsRead()}
                  title="Mark all as read"
                >
                  <CheckCheck size={16} />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  type="button"
                  className="clear-all-btn"
                  onClick={() => clearAll()}
                  disabled={isDeleting}
                  title="Clear all"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                type="button"
                className="close-dropdown-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="notification-filters" role="tablist">
            {NOTIFICATION_FILTERS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeFilter === id}
                className={`notification-filter-btn ${activeFilter === id ? 'active' : ''}`}
                onClick={() => setActiveFilter(id)}
              >
                {id === 'all' ? 'All' : id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            ))}
          </div>

          <div className="notification-list">
            {isLoading ? (
              <div className="notification-loading">
                <Loader2 size={24} className="spin" />
                <p>Loading...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="notification-empty">
                <Bell size={40} />
                <p>No notifications</p>
                <span>You&apos;re all caught up</span>
              </div>
            ) : (
              <>
                {renderGroup('Today', groups.today)}
                {renderGroup('Yesterday', groups.yesterday)}
                {renderGroup('Older', groups.older)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default NotificationDropdown;
